import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../contexts/AppContext";
import BuildCampaignModal from "../components/campaigns/BuildCampaignModal";

const getStatusClass = (status) => {
  if (status === "active") return "active";
  if (status === "completed") return "completed";
  if (status === "paused") return "paused";
  return "pending";
};

export default function CampaignsPage() {
  const {
    campaigns,
    campaignsLoading,
    campaignsError,
    refreshCampaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    currentUserType,
    currentUser,
    getCampaignStatistics,
  } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(new Set());
  const requestedStatsRef = useRef(new Set());

  const handleCreate = () => {
    setEditingCampaign(null);
    setIsModalOpen(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleDelete = async (campaign) => {
    const confirmed =
      typeof window !== "undefined"
        ? window.confirm(`Delete campaign "${campaign.name}"?`)
        : true;

    if (!confirmed) return;

    try {
      await deleteCampaign(campaign.uuid);
    } catch (error) {
      console.error("Failed to delete campaign", error);
      if (typeof window !== "undefined") {
        window.alert("Failed to delete campaign. Please try again.");
      }
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editingCampaign) {
        await updateCampaign(editingCampaign.uuid, payload);
      } else {
        await addCampaign({
          ...payload,
          createdBy: currentUser || "admin",
        });
      }
      setIsModalOpen(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error("Failed to save campaign", error);
      if (typeof window !== "undefined") {
        window.alert(
          "Failed to save campaign. Please review the fields and try again.",
        );
      }
    }
  };

  const loadCampaignStatistics = useCallback(async (campaignUuid) => {
    if (!campaignUuid || loadingStats.has(campaignUuid) || requestedStatsRef.current.has(campaignUuid)) {
      return;
    }

    requestedStatsRef.current.add(campaignUuid);
    setLoadingStats(prev => new Set(prev).add(campaignUuid));
    try {
      const stats = await getCampaignStatistics(campaignUuid);
      setCampaignStats(prev => ({
        ...prev,
        [campaignUuid]: stats,
      }));
    } catch (error) {
      console.error(`Failed to load statistics for campaign ${campaignUuid}`, error);
      setCampaignStats(prev => ({
        ...prev,
        [campaignUuid]: { totalReceivers: 0, uniqueReceivers: 0 },
      }));
    } finally {
      setLoadingStats(prev => {
        const next = new Set(prev);
        next.delete(campaignUuid);
        return next;
      });
    }
  }, [getCampaignStatistics, loadingStats]);

  useEffect(() => {
    if (campaigns.length > 0 && !campaignsLoading) {
      campaigns.forEach((campaign, index) => {
        if (campaign.uuid && !requestedStatsRef.current.has(campaign.uuid)) {
          setTimeout(() => {
            loadCampaignStatistics(campaign.uuid);
          }, index * 50);
        }
      });
    }
  }, [campaigns, campaignsLoading]);

  return (
    <section className="content-section active">
      <div className="section-header">
        <div>
          <h2>Campaigns</h2>
          <p>Manage live, scheduled, and completed campaigns</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Add Campaign
        </button>
      </div>

      {campaignsError && campaigns.length === 0 ? (
        <div className="empty-state">
          <h3>Unable to load campaigns</h3>
          <p>Please refresh or try again later.</p>
          <button className="btn btn-secondary" onClick={refreshCampaigns}>
            Retry
          </button>
        </div>
      ) : campaignsLoading && campaigns.length === 0 ? (
        <div className="empty-state">
          <h3>Loading campaigns…</h3>
          <p>Fetching the latest campaign data.</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="empty-state">
          <h3>No Campaigns</h3>
          <p>Click "Add Campaign" to create your first campaign</p>
        </div>
      ) : (
        <div className="campaigns-grid">
          <table className="campaigns-table">
            <thead>
              <tr>
                <th>Campaign Name</th>
                {currentUserType === "admin" && <th>Created By</th>}
                <th>Status</th>
                <th>Total Impressions</th>
                <th>Unique Devices</th>
                <th>Creative</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => (
                <tr key={campaign.uuid}>
                  <td className="campaign-name-cell">{campaign.name}</td>
                  {currentUserType === "admin" && <td>{campaign.createdBy}</td>}
                  <td>
                    <span
                      className={`campaign-status ${getStatusClass(campaign.status)}`}
                    >
                      {campaign.status}
                    </span>
                  </td>
                  <td>
                    {loadingStats.has(campaign.uuid) ? (
                      <span style={{ opacity: 0.6 }}>Loading...</span>
                    ) : (
                      (campaignStats[campaign.uuid]?.totalReceivers ?? 0).toLocaleString()
                    )}
                  </td>
                  <td>
                    {loadingStats.has(campaign.uuid) ? (
                      <span style={{ opacity: 0.6 }}>Loading...</span>
                    ) : (
                      (campaignStats[campaign.uuid]?.uniqueReceivers ?? 0).toLocaleString()
                    )}
                  </td>
                  <td>
                    {campaign.creativeUrl ? (
                      <div className="campaign-creative-meta">
                        <div className="creative-url">
                          {campaign.creativeUrl}
                        </div>
                        <div className="creative-specs">
                          {campaign.creativeDuration
                            ? `${campaign.creativeDuration}s`
                            : "—"}{" "}
                          ·
                          {campaign.creativeWidth && campaign.creativeHeight
                            ? ` ${campaign.creativeWidth}x${campaign.creativeHeight}px`
                            : " resolution"}
                        </div>
                      </div>
                    ) : (
                      <span className="creative-specs muted">No creative</span>
                    )}
                  </td>
                  <td className="campaign-actions-cell">
                    <button
                      className="btn-table-action"
                      onClick={() => handleEdit(campaign)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-table-action danger"
                      onClick={() => handleDelete(campaign)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BuildCampaignModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCampaign(null);
        }}
        initialCampaign={editingCampaign}
        onSave={handleSave}
      />
    </section>
  );
}
